import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from './ui/Button';
import TextInput from './ui/TextInput';
import { Label } from './ui/Label';
import { cn } from '../lib/utils';
import { APP_BRAND } from '../config/branding';
import AppBrandIcon from './branding/AppBrandIcon';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface ValidateResponse {
  email: string;
  valid: boolean;
  expiresAt?: string;
}

const COMM_FEATURES = [
  { label: 'Email', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  {
    label: 'Address book',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  },
  {
    label: 'Appointments',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    label: 'Todos',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
] as const;

interface InvitationLayoutProps {
  productName: string;
  children: React.ReactNode;
}

const InvitationLayout: React.FC<InvitationLayoutProps> = ({ productName, children }) => {
  const { colors } = useTheme();

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="relative flex flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-sky-950 to-indigo-950 px-8 py-10 lg:w-[44%] lg:min-h-screen lg:px-12 lg:py-14">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          aria-hidden
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(56,189,248,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(99,102,241,0.3) 0%, transparent 40%)',
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-4">
            <AppBrandIcon className="h-12 w-12" iconClassName="h-6 w-6" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-white">{productName}</h1>
            </div>
          </div>
          <p className="mt-8 max-w-md text-base leading-relaxed text-slate-300">
            One workspace for email, contacts, scheduling, and follow-ups — built for teams that communicate with
            clients every day.
          </p>
          <ul className="mt-8 grid grid-cols-2 gap-3 sm:max-w-md">
            {COMM_FEATURES.map(({ label, icon }) => (
              <li
                key={label}
                className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-slate-100 backdrop-blur-sm"
              >
                <svg className="h-4 w-4 shrink-0 text-sky-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={icon} />
                </svg>
                {label}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 mt-10 text-xs text-slate-500 lg:mt-0">
          © {new Date().getFullYear()} {productName}.
        </p>
      </div>

      {/* Form panel */}
      <div className={cn('flex flex-1 items-center justify-center px-4 py-10 sm:px-8', colors.background)}>
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-3">
              <AppBrandIcon className="h-11 w-11" iconClassName="h-5 w-5" />
              <div>
                <p className={cn('text-lg font-bold', colors.text)}>{productName}</p>
              </div>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

const RegisterFromInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { appConfig } = useSettings();

  const productName = appConfig.system.appName?.trim() || APP_BRAND.name;

  const [status, setStatus] = useState<'loading' | 'invalid' | 'valid'>('loading');
  const [email, setEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle(
    status === 'valid' ? 'Create account' : status === 'invalid' ? 'Invalid invitation' : undefined,
  );

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<ValidateResponse>(`/auth/invitation/${token}`);
        if (cancelled) return;
        if (data.valid) {
          setEmail(data.email);
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch {
        if (!cancelled) setStatus('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!userPassword.trim() || userPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!userFirstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!userLastName.trim()) {
      setError('Last name is required.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/register-with-invitation', {
        token,
        userPassword: userPassword.trim(),
        userFirstName: userFirstName.trim(),
        userLastName: userLastName.trim(),
        userDisplayName: userDisplayName.trim() || undefined,
      });
      navigate('/login?registered=1');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Registration failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <InvitationLayout productName={productName}>
        <div className={cn('rounded-2xl border p-8 shadow-sm text-center', colors.surface, colors.border)}>
          <p className={cn('text-sm', colors.textMuted)}>Checking invitation…</p>
        </div>
      </InvitationLayout>
    );
  }

  if (status === 'invalid') {
    return (
      <InvitationLayout productName={productName}>
        <div className={cn('rounded-2xl border p-8 shadow-sm text-center', colors.surface, colors.border)}>
          <h2 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>Invalid or expired invitation</h2>
          <p className={cn('mt-2 text-sm', colors.textMuted)}>
            This link may have expired or already been used. Please request a new invitation.
          </p>
          <Button type="button" className="mt-6" onClick={() => navigate('/login')}>
            Go to login
          </Button>
        </div>
      </InvitationLayout>
    );
  }

  return (
    <InvitationLayout productName={productName}>
      <div className={cn('rounded-2xl border p-8 shadow-sm', colors.surface, colors.border)}>
        <div className="mb-8">
          <h2 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>Create your account</h2>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            Complete the form below. Your email is from your invitation.
          </p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.error)} role="alert">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <TextInput
                id="email"
                type="email"
                value={email}
                readOnly
                className={cn('mt-1.5', colors.surfaceHover)}
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <TextInput
                id="password"
                type="password"
                value={userPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                placeholder="Enter your password (min 6 characters)"
                autoComplete="new-password"
                disabled={loading}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="firstName">First name</Label>
              <TextInput
                id="firstName"
                type="text"
                value={userFirstName}
                onChange={(e) => setUserFirstName(e.target.value)}
                placeholder="First name"
                autoComplete="given-name"
                disabled={loading}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="lastName">Last name</Label>
              <TextInput
                id="lastName"
                type="text"
                value={userLastName}
                onChange={(e) => setUserLastName(e.target.value)}
                placeholder="Last name"
                autoComplete="family-name"
                disabled={loading}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="displayName">Display name (optional)</Label>
              <TextInput
                id="displayName"
                type="text"
                value={userDisplayName}
                onChange={(e) => setUserDisplayName(e.target.value)}
                placeholder="Display name"
                autoComplete="nickname"
                disabled={loading}
                className="mt-1.5"
              />
            </div>
          </div>

          <Button type="submit" className="w-full" loading={loading} disabled={loading}>
            Create account
          </Button>
        </form>

        <p className={cn('mt-6 text-center text-sm', colors.textMuted)}>
          Already have an account?{' '}
          <Link to="/login" className={cn('font-medium hover:underline', colors.primaryFg)}>
            Sign in
          </Link>
        </p>
      </div>
    </InvitationLayout>
  );
};

export default RegisterFromInvitation;
